import { SiteFooter, LandingHeader } from '@/components/landing';
import { LANDING_HEADER_HEIGHT } from '@/components/landing/landing-header.component';
import { AppHeader } from '@/components/layouts/app-header.component';
import { LanguageSwitcher } from '@/components/language-switcher/language-switcher.component';
import { ThemeToggle } from '@/components/theme-toggle.component';
import { DASHBOARD_PATH } from '@/constants/pages';
import { useSession } from '@/hooks/use-session.hook';
import { Box, Button, Stack } from '@mui/material';
import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

type PublicLayoutProps = PropsWithChildren<{
  title: string;
  description?: string;
}>;

export const PublicLayout: FunctionComponent<PublicLayoutProps> = ({
  title,
  description,
  children
}) => {
  const { t } = useTranslation();
  const { isLoading, session } = useSession({ skipRedirect: true });
  const isLoggedIn = !isLoading && !!session;

  return (
    <React.Fragment>
      <Head>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
      </Head>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {isLoggedIn ? (
          <AppHeader logoHref={DASHBOARD_PATH}>
            <Stack
              sx={{
                flexDirection: 'row',
                gap: 1,
                alignItems: 'center',
                ml: 'auto'
              }}
            >
              <LanguageSwitcher />
              <ThemeToggle />
              <Button
                component={Link}
                href={DASHBOARD_PATH}
                variant="contained"
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                {t('nav:dashboard')}
              </Button>
            </Stack>
          </AppHeader>
        ) : (
          <LandingHeader />
        )}

        <Box sx={{ pt: isLoggedIn ? { xs: 2, sm: 3 } : LANDING_HEADER_HEIGHT }}>
          {!isLoading && children}
        </Box>

        <SiteFooter />
      </Box>
    </React.Fragment>
  );
};
